import { useDebounceFn, useUpdateEffect } from 'ahooks';
import React from 'react';
import { Box, Input, Textarea } from '@apia/theme';
import parameters from '../../../../../config/parameters';
import { useAppSelector } from '../../../../../hooks/hooks';
import store from '../../../../../store';
import { translationActions } from '../../../../../store/translationSlice';
import validationsStore, {
  useFieldSelector,
} from '../../../../../store/validation/validationStore';
import { useFormContext } from '../../../../common/validation/FormContext';
import TranslateEditor from './TranslateEditor';
import { ILanguageState } from './LanguagesList';

interface ITranslateField {
  type: string;
  fieldId: string;
}

const TranslateField = ({ type, fieldId }: ITranslateField) => {
  const { name } = useFormContext();
  const browserHeight = window.innerHeight;
  const selectedLangId = useAppSelector(
    (global) =>
      global.translationSlice[fieldId]?.selectedLanguage?.id as string,
  );
  const storeValue = useFieldSelector(name, selectedLangId, {
    selector: (props) => {
      return (
        (props?.value as string) ??
        (store
          .getState()
          .translationSlice[
            fieldId
          ]?.langs.find((current) => String(current.id) === String(selectedLangId))
          ?.currentValue as string)
      );
    },
    initialValue: '',
  });
  const langs = useAppSelector(
    (global) =>
      (global.translationSlice[fieldId]?.langs ??
        []) as unknown as ILanguageState[],
  );

  const currentValue = langs.find(
    (current) => String(current.id) === String(selectedLangId),
  )?.currentValue as string;

  const { run, flush } = useDebounceFn(
    React.useCallback(
      (newValue: string) => {
        if ((newValue || newValue === '') && !!selectedLangId) {
          validationsStore.setFieldValue(
            name,
            String(selectedLangId),
            newValue,
          );
          store.dispatch(
            translationActions.markAsQueried({
              hasQueriedServer: true,
              id: fieldId,
              langId: selectedLangId,
            }),
          );
        }
      },
      [selectedLangId, name, fieldId],
    ),
    { wait: parameters.timing.keyboardFilterDebounceTime },
  );

  const setLocalValueHandler = (newValue: string) => {
    setLocalValue(newValue);
  };

  const [localValue, setLocalValue] = React.useState(storeValue ?? '');
  useUpdateEffect(() => {
    run(localValue);
    if (localValue !== '' && localValue !== currentValue) {
      store.dispatch(
        translationActions.markIsTemporalyTranslated({
          isTemporalyTranslated: true,
          id: fieldId,
          langId: selectedLangId,
        }),
      );
      store.dispatch(
        translationActions.markIsTranslated({
          isTranslated: false,
          id: fieldId,
          langId: selectedLangId,
        }),
      );
    } else if (storeValue === currentValue && localValue !== '') {
      store.dispatch(
        translationActions.markIsTemporalyTranslated({
          isTemporalyTranslated: false,
          id: fieldId,
          langId: selectedLangId,
        }),
      );
      store.dispatch(
        translationActions.markIsTranslated({
          isTranslated: true,
          id: fieldId,
          langId: selectedLangId,
        }),
      );
    } else {
      store.dispatch(
        translationActions.markIsTemporalyTranslated({
          isTemporalyTranslated: false,
          id: fieldId,
          langId: selectedLangId,
        }),
      );
      store.dispatch(
        translationActions.markIsTranslated({
          isTranslated: false,
          id: fieldId,
          langId: selectedLangId,
        }),
      );
    }
  }, [localValue, run]);

  React.useEffect(() => {
    setLocalValue(storeValue ?? '');
  }, [fieldId, selectedLangId, storeValue]);
  let typeForClass = '';
  let fieldContent;

  switch (type) {
    case 'input':
      fieldContent = (
        <Input
          maxLength={255}
          className="languageTranslate__translateField translateField__input"
          value={localValue}
          onBlur={flush}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key.toLowerCase() === 'enter') {
              flush();
            }
          }}
          onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
            setLocalValue(ev.target.value);
          }}
        />
      );
      break;
    case 'area':
      fieldContent = (
        <Textarea
          className={`languageTranslate__translateField translateField__textArea ${
            browserHeight > 600 ? 'hasMore' : ''
          }`}
          value={localValue}
          onBlur={flush}
          onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key.toLowerCase() === 'enter') {
              flush();
            }
          }}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
            setLocalValue(e.target.value);
          }}
          rows={8}
        />
      );
      break;
    case 'editor':
      fieldContent = (
        <TranslateEditor
          value={localValue}
          onSetLocalValue={(newValue: string) => setLocalValueHandler(newValue)}
          onFlush={flush}
        />
      );
      typeForClass = 'editor';
      break;
    default:
  }

  return (
    <Box
      className={` ${
        typeForClass === 'editor'
          ? 'languageTranslate__translateField__fieldCointainer__hasEditor'
          : 'languageTranslate__translateField__fieldCointainer'
      }`}
    >
      <Box as="span" sx={React.useMemo(() => ({ fontWeight: 'bold' }), [])}>
        {' '}
        {window.LBL_TRANSLATION_FIELD}
      </Box>
      {fieldContent}
    </Box>
  );
};

export default TranslateField;
